import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { publicApi } from "@/lib/api";
import { ArrowRight, MousePointerClick, Globe2, UserPlus, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-white text-[#111827]">
      <header className="border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <RouterLink to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="h-9 w-9 rounded-md bg-[#111827] flex items-center justify-center">
              <span className="font-display font-black text-white">T</span>
            </div>
            <div>
              <div className="font-display font-black tracking-tight">TRACE</div>
              <div className="label-eyebrow text-[#4B5563]">Affiliate Tracker</div>
            </div>
          </RouterLink>
          <nav className="flex items-center gap-3">
            {user ? (
              <RouterLink to="/dashboard" data-testid="header-dashboard-link" className="btn-primary">Dashboard <ArrowRight size={14} /></RouterLink>
            ) : (
              <>
                <RouterLink to="/login" data-testid="header-login-link" className="text-sm font-bold hover:text-[#002FA7]">Sign in</RouterLink>
                <RouterLink to="/register" data-testid="header-register-link" className="btn-primary">Get started <ArrowRight size={14} /></RouterLink>
              </>
            )}
          </nav>
        </div>
      </header>

      <VideoCarouselSection />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="label-eyebrow text-[#002FA7] mb-5" data-testid="landing-eyebrow">— Affiliate links · Live tracking · Lead capture</div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tight font-black leading-[0.95]">
                Your name<br />in every link.<br />
                <span className="text-[#002FA7]">Every lead captured.</span>
              </h1>
              <p className="mt-6 text-lg text-[#4B5563] max-w-xl">
                Generate trackable affiliate links that include your own name, watch clicks roll in by country in real time, and capture every visitor through a beautifully simple popup before they reach the destination.
              </p>
              <div className="mt-8 flex items-center gap-3 flex-wrap">
                <RouterLink to="/register" data-testid="hero-cta-register" className="btn-primary">
                  Create your first link <ArrowRight size={16} />
                </RouterLink>
                <RouterLink to="/login" data-testid="hero-cta-login" className="btn-secondary">I already have an account</RouterLink>
              </div>
              <div className="mt-10 flex items-center gap-6 text-sm text-[#4B5563]">
                <div><span className="font-display font-black text-[#111827] text-2xl">100%</span><br/>free during beta</div>
                <div className="h-10 w-px bg-[#E5E7EB]" />
                <div><span className="font-display font-black text-[#111827] text-2xl">~50ms</span><br/>click resolution</div>
                <div className="h-10 w-px bg-[#E5E7EB]" />
                <div><span className="font-display font-black text-[#111827] text-2xl">200+</span><br/>countries traced</div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="card-flat p-6 fade-up fade-up-delay-1">
                <div className="label-eyebrow text-[#4B5563] mb-3">Your link, ready in 1 click</div>
                <div className="font-mono text-sm bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-3 break-all">
                  trace.app/r/<span className="text-[#002FA7] font-bold">ada-lovelace</span>-x7k2qz
                </div>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  <Stat label="Clicks" value="1,284" />
                  <Stat label="Leads" value="312" accent />
                  <Stat label="Conv." value="24.3%" />
                </div>
                <div className="divider-thin my-6" />
                <div className="flex items-center gap-2 text-xs text-[#4B5563]">
                  <Sparkles size={14} className="text-[#002FA7]" />
                  Real-time. Country-aware. No coding.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-3 gap-6">
            <Feature icon={UserPlus} title="Sign up — your name becomes the link" body="When you register, every link you create includes a slug derived from your name. Customers see who's referring them." />
            <Feature icon={MousePointerClick} title="Track every click" body="Time, date, IP-derived country, city, referrer and user agent — all logged the moment someone opens the link." />
            <Feature icon={Globe2} title="Capture before redirect" body="A glassmorphism popup asks the visitor's name, email, delivery location, and discount preference, then forwards them to your destination." />
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex items-center justify-between text-sm text-[#4B5563]">
          <span className="font-display font-bold text-[#111827]">TRACE</span>
          <span>© 2026</span>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="label-eyebrow text-[#4B5563]">{label}</div>
      <div className={`font-display font-black text-2xl ${accent ? "text-[#002FA7]" : "text-[#111827]"}`}>{value}</div>
    </div>
  );
}

function Feature({ icon: Icon, title, body }) {
  return (
    <div className="card-flat p-6">
      <div className="h-10 w-10 rounded-md bg-[#111827] text-white flex items-center justify-center mb-4">
        <Icon size={18} />
      </div>
      <h3 className="font-display font-black text-xl tracking-tight mb-2">{title}</h3>
      <p className="text-sm text-[#4B5563] leading-relaxed">{body}</p>
    </div>
  );
}

function VideoCarouselSection() {
  const [videos, setVideos] = useState(null);
  const trackRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get("/landing/videos");
        setVideos(Array.isArray(data) ? data : []);
      } catch {
        setVideos([]);
      }
    })();
  }, []);

  if (videos === null || videos.length === 0) return null;

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-video-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section className="border-t border-[#E5E7EB] bg-[#FAFAFA]" data-testid="landing-video-carousel">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow text-[#002FA7]">Watch</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-[#111827]">See it in action</h2>
          </div>
          {videos.length > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => scrollBy(-1)}
                data-testid="video-carousel-prev"
                aria-label="Previous video"
                className="h-10 w-10 rounded-full bg-white border border-[#E5E7EB] hover:border-[#111827] inline-flex items-center justify-center text-[#111827] transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => scrollBy(1)}
                data-testid="video-carousel-next"
                aria-label="Next video"
                className="h-10 w-10 rounded-full bg-white border border-[#E5E7EB] hover:border-[#111827] inline-flex items-center justify-center text-[#111827] transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>

        <div
          ref={trackRef}
          className="flex gap-6 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin"
          style={{ scrollbarWidth: "thin" }}
        >
          {videos.map((v) => (
            <div
              key={v.video_id}
              data-video-card
              data-testid={`landing-video-${v.video_id}`}
              className="snap-start flex-shrink-0 w-[88%] sm:w-[60%] lg:w-[48%]"
            >
              <div className="aspect-video rounded-xl overflow-hidden border border-[#E5E7EB] bg-black shadow-sm">
                <iframe
                  className="w-full h-full"
                  src={v.embed_url}
                  title={v.title || "YouTube video"}
                  frameBorder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
              {v.title && (
                <div className="mt-3 font-display font-bold text-lg text-[#111827]">{v.title}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
